// frontend/src/pages/Profile/MyProfile.jsx
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { getVendor, updateVendor, uploadVendorDocument, getVendorDocumentUrl } from '../../api/vendor.api.js';
import { getPartner, updatePartner, uploadPartnerDocument, getPartnerDocumentUrl } from '../../api/partner.api.js';
import {
  PageHeader, Spinner, FormField, ErrorMessage,
  DocTypeLabel, formatDate, formatFileSize,
} from '../../components/ui';
import { Save, Upload, Download } from 'lucide-react';

const DOC_TYPES = ['AKTA', 'SK_KEMENKUMHAM', 'NIB', 'NPWP', 'SHAREHOLDER_STRUCTURE'];

export default function MyProfile() {
  const { user, isVendor } = useAuth();
  const qc       = useQueryClient();
  const fileRef  = useRef(null);

  const [editMode,    setEditMode]    = useState(false);
  const [form,        setForm]        = useState({});
  const [uploadType,  setUploadType]  = useState('AKTA');
  const [formError,   setFormError]   = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const profileId  = user?.userId;
  const getProfile = isVendor ? () => getVendor(profileId) : () => getPartner(profileId);
  const doUpdate   = isVendor ? (data) => updateVendor(profileId, data)  : (data) => updatePartner(profileId, data);
  const doUpload   = isVendor ? uploadVendorDocument  : uploadPartnerDocument;
  const getDocUrl  = isVendor ? getVendorDocumentUrl  : getPartnerDocumentUrl;

  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-profile', profileId],
    queryFn:  getProfile,
  });

  const updateProfile = useMutation({
    mutationFn: doUpdate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-profile', profileId] });
      setEditMode(false);
      setFormError(null);
    },
    onError: (err) => setFormError(err),
  });

  const uploadDoc = useMutation({
    mutationFn: ({ file, type }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type);
      return doUpload(profileId, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-profile', profileId] });
      setUploadError(null);
      if (fileRef.current) fileRef.current.value = '';
    },
    onError: (err) => setUploadError(err),
  });

  async function downloadDoc(docId) {
    try {
      const { url } = await getDocUrl(profileId, docId);
      window.open(url, '_blank');
    } catch (err) {
      alert(err.message);
    }
  }

  function set(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.value })); }

  function startEdit() {
    setForm({
      companyName: profile?.companyName ?? '',
      picName:     profile?.picName     ?? '',
      picPhone:    profile?.picPhone    ?? '',
      npwp:        profile?.npwp        ?? '',
      address:     profile?.address     ?? '',
    });
    setEditMode(true);
  }

  if (isLoading) return <Spinner />;

  const docsByType = DOC_TYPES.reduce((acc, t) => {
    acc[t] = (profile?.documents ?? []).filter((d) => d.type === t);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        title="My Profile"
        subtitle={isVendor ? 'Vendor account' : 'Partner account'}
        action={!editMode && (
          <button onClick={startEdit} className="btn-secondary text-sm">Edit Profile</button>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Profile info */}
        <div className="lg:col-span-2 space-y-4">

          {/* Verification banner for vendors */}
          {isVendor && (
            <div className={`card p-4 flex items-center gap-3 ${
              profile?.isVerified
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                profile?.isVerified ? 'bg-green-500' : 'bg-amber-500'
              }`} />
              <div>
                <p className={`text-sm font-medium ${profile?.isVerified ? 'text-green-700' : 'text-amber-700'}`}>
                  {profile?.isVerified ? 'Account Verified' : 'Pending Verification'}
                </p>
                <p className={`text-xs ${profile?.isVerified ? 'text-green-600' : 'text-amber-600'}`}>
                  {profile?.isVerified
                    ? 'You can submit agreements and addendums.'
                    : 'The Legal team will verify your account after reviewing your documents.'}
                </p>
              </div>
            </div>
          )}

          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Company Information</h2>

            {editMode ? (
              <div className="space-y-4">
                {formError && <ErrorMessage error={formError} />}

                <FormField label="Company Name" required>
                  <input className="input" value={form.companyName} onChange={set('companyName')} required />
                </FormField>

                <div className="grid grid-cols-2 gap-3">
                  <FormField label="PIC Name" required>
                    <input className="input" value={form.picName} onChange={set('picName')} required />
                  </FormField>
                  <FormField label="PIC Phone">
                    <input className="input" type="tel" value={form.picPhone} onChange={set('picPhone')} />
                  </FormField>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FormField label="NPWP">
                    <input className="input" value={form.npwp} onChange={set('npwp')} />
                  </FormField>
                  <FormField label="Address">
                    <input className="input" value={form.address} onChange={set('address')} />
                  </FormField>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => updateProfile.mutate(form)}
                    disabled={updateProfile.isPending}
                    className="btn-primary flex items-center gap-2 text-sm"
                  >
                    <Save size={14} />
                    {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditMode(false)} className="btn-secondary text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ['Company Name', profile?.companyName],
                  ['Email',        profile?.email],
                  ['PIC Name',     profile?.picName],
                  ['PIC Phone',    profile?.picPhone],
                  ['NPWP',         profile?.npwp],
                  ['Address',      profile?.address],
                  ['Registered',   isVendor ? formatDate(profile?.registeredAt) : formatDate(profile?.createdAt)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{k}</dt>
                    <dd className="text-gray-900 font-medium">{v || '—'}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>

        {/* Documents */}
        <div>
          <div className="card">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">My Documents</h2>
            </div>

            {/* Upload controls */}
            <div className="px-5 py-3 border-b border-gray-50 flex gap-2">
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="input py-1.5 text-xs flex-1"
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploadDoc.isPending}
                className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3 flex-shrink-0"
              >
                <Upload size={13} />
                {uploadDoc.isPending ? '...' : 'Upload'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadDoc.mutate({ file, type: uploadType });
                }}
              />
            </div>

            {uploadError && (
              <div className="px-5 pt-3"><ErrorMessage error={uploadError} /></div>
            )}

            <div className="px-5 py-4 space-y-4">
              {DOC_TYPES.map((type) => (
                <div key={type}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                    <DocTypeLabel type={type} />
                  </p>
                  {docsByType[type].length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Not uploaded</p>
                  ) : docsByType[type].map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg mb-1">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">{doc.fileName}</p>
                        <p className="text-xs text-gray-400">{formatFileSize(doc.fileSize)}</p>
                      </div>
                      <button
                        onClick={() => downloadDoc(doc.id)}
                        className="ml-2 text-brand-500 hover:text-brand-700 flex-shrink-0"
                      >
                        <Download size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
